import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const NetworkDetailModal = ({ node, onClose }) => {
  if (!node) return null;

  const levelInfo = {
    1: { commission: '10%', color: 'text-primary', bgColor: 'bg-primary/10' },
    2: { commission: '4%', color: 'text-accent', bgColor: 'bg-accent/10' },
    3: { commission: '0.6%', color: 'text-secondary', bgColor: 'bg-secondary/10' },
    4: { commission: '0.4%', color: 'text-warning', bgColor: 'bg-warning/10' }
  };

  const info = levelInfo?.[node?.level] || levelInfo?.[1];

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-card rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-foreground">
            Detalles del Referido
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl hover:bg-muted transition-smooth flex items-center justify-center"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Profile Section */}
          <div className="flex items-start space-x-4">
            <Image
              src={node?.avatar}
              alt={node?.avatarAlt}
              className="w-20 h-20 rounded-full object-cover"
            />
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-xl font-semibold text-foreground">
                  {node?.name}
                </h3>
                <div className={`
                  px-3 py-1 rounded-lg text-sm font-medium
                  ${node?.isActive ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'}
                `}>
                  {node?.isActive ? 'Activo' : 'Inactivo'}
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-1">
                Registrado: {node?.registrationDate}
              </p>
              <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-lg ${info?.bgColor}`}>
                <span className={`text-sm font-medium ${info?.color}`}>
                  Nivel {node?.level} • Comisión {info?.commission}
                </span>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <Icon name="Users" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{node?.totalReferrals}</p>
              <p className="text-xs text-muted-foreground">Total Referidos</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <Icon name="ShoppingCart" size={24} color="var(--color-accent)" className="mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{node?.monthlySales}</p>
              <p className="text-xs text-muted-foreground">Ventas del Mes</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <Icon name="DollarSign" size={24} color="var(--color-success)" className="mx-auto mb-2" />
              <p className="text-2xl font-bold text-success">
                S/ {node?.monthlyCommission?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground">Comisión Mes</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-4 text-center">
              <Icon name="TrendingUp" size={24} color="var(--color-warning)" className="mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">
                S/ {node?.totalCommission?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground">Total Histórico</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t border-border pt-6">
            <h4 className="text-lg font-semibold text-foreground mb-4">
              Información de Contacto
            </h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Mail" size={20} color="var(--color-muted-foreground)" />
                <span className="text-sm text-foreground">{node?.email}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="Phone" size={20} color="var(--color-muted-foreground)" />
                <span className="text-sm text-foreground">{node?.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Icon name="MapPin" size={20} color="var(--color-muted-foreground)" />
                <span className="text-sm text-foreground">{node?.location}</span>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="border-t border-border pt-6">
            <h4 className="text-lg font-semibold text-foreground mb-4">
              Actividad Reciente
            </h4>
            <div className="space-y-3">
              {node?.recentActivity?.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50">
                  <Icon name={activity?.icon} size={20} color="var(--color-primary)" />
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{activity?.description}</p>
                    <p className="text-xs text-muted-foreground">{activity?.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex flex-col md:flex-row gap-3">
          <Button
            variant="outline"
            iconName="MessageCircle"
            iconPosition="left"
            fullWidth
          >
            Enviar Mensaje
          </Button>
          <Button
            variant="default"
            iconName="Eye"
            iconPosition="left"
            fullWidth
          >
            Ver Perfil Completo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NetworkDetailModal;